/**
 * Utility functions for handling tenant-scoped operations
 */

/**
 * Filter data by tenant ID
 * @param {Array} data - Array of objects with tenant_id property
 * @param {string} tenantId - Current tenant ID
 * @returns {Array} Filtered data
 */
export const filterByTenant = (data, tenantId) => {
  if (!Array.isArray(data) || !tenantId) return [];
  return data.filter(item => item.tenant_id === tenantId);
};

/**
 * Validate if a resource belongs to a specific tenant
 * @param {Object} resource - Resource object with tenant_id property
 * @param {string} tenantId - Current tenant ID
 * @returns {boolean} True if resource belongs to tenant
 */
export const validateTenantResource = (resource, tenantId) => {
  if (!resource || !tenantId) return false;
  return resource.tenant_id === tenantId;
};

/**
 * Add tenant ID to data object
 * @param {Object} data - Data object
 * @param {string} tenantId - Tenant ID to add
 * @returns {Object} Data with tenant_id
 */
export const addTenantId = (data, tenantId) => {
  if (!tenantId) return data;
  return {
    ...data,
    tenant_id: tenantId
  };
};

/**
 * Remove tenant ID from data object
 * @param {Object} data - Data object
 * @returns {Object} Data without tenant_id
 */
export const removeTenantId = (data) => {
  const { tenant_id, ...cleanData } = data;
  return cleanData;
};

/**
 * Check if user has permission to access a feature
 * @param {Array} userFeatures - User's available features
 * @param {string} requiredFeature - Feature to check
 * @returns {boolean} True if user has access
 */
export const hasFeatureAccess = (userFeatures, requiredFeature) => {
  if (!Array.isArray(userFeatures) || !requiredFeature) return false;
  return userFeatures.includes(requiredFeature);
};

/**
 * Check if user can perform an action based on tenant limits
 * @param {Object} tenant - Tenant object
 * @param {Object} usage - Current usage object
 * @param {string} resourceType - Type of resource (users, locations, etc.)
 * @returns {boolean} True if action is allowed
 */
export const canPerformAction = (tenant, usage, resourceType) => {
  if (!tenant || !usage) return false;
  
  const limitKey = `${resourceType}_limit`;
  const usageKey = `${resourceType}_count`;
  
  if (tenant[limitKey] === -1) return true; // Unlimited
  if (tenant[limitKey] === undefined) return true; // No limit set
  
  return (usage[usageKey] || 0) < tenant[limitKey];
};

/**
 * Get remaining capacity for a resource type
 * @param {Object} tenant - Tenant object
 * @param {Object} usage - Current usage object
 * @param {string} resourceType - Type of resource
 * @returns {number} Remaining capacity (-1 for unlimited)
 */
export const getRemainingCapacity = (tenant, usage, resourceType) => {
  if (!tenant || !usage) return 0;
  
  const limitKey = `${resourceType}_limit`;
  const usageKey = `${resourceType}_count`;
  
  if (tenant[limitKey] === -1) return -1; // Unlimited
  if (tenant[limitKey] === undefined) return 0; // No limit set
  
  return Math.max(0, tenant[limitKey] - (usage[usageKey] || 0));
};

/**
 * Format tenant status for display
 * @param {string} status - Tenant status
 * @returns {Object} Formatted status object
 */
export const formatTenantStatus = (status) => {
  const statusMap = {
    active: { label: 'Active', color: 'green', icon: 'check-circle' },
    inactive: { label: 'Inactive', color: 'gray', icon: 'x-circle' },
    trial: { label: 'Trial', color: 'blue', icon: 'clock' },
    suspended: { label: 'Suspended', color: 'red', icon: 'pause-circle' }
  };
  
  return statusMap[status] || statusMap.inactive;
};

/**
 * Check if tenant is in trial period
 * @param {Object} tenant - Tenant object
 * @returns {boolean} True if tenant is in trial
 */
export const isTenantInTrial = (tenant) => {
  if (!tenant) return false;
  
  if (tenant.status === 'trial') {
    if (tenant.trial_ends_at) {
      return new Date(tenant.trial_ends_at) > new Date();
    }
    return true;
  }
  
  return false;
};

/**
 * Get days remaining in trial
 * @param {Object} tenant - Tenant object
 * @returns {number} Days remaining (-1 if no trial or expired)
 */
export const getTrialDaysRemaining = (tenant) => {
  if (!isTenantInTrial(tenant) || !tenant.trial_ends_at) return -1;
  
  const trialEnd = new Date(tenant.trial_ends_at);
  const now = new Date();
  const diffTime = trialEnd - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

/**
 * Check if tenant subscription is active
 * @param {Object} tenant - Tenant object
 * @returns {boolean} True if subscription is active
 */
export const isSubscriptionActive = (tenant) => {
  if (!tenant) return false;
  
  if (tenant.status === 'active') return true;
  if (tenant.status === 'trial') return isTenantInTrial(tenant);
  
  if (tenant.subscription_ends_at) {
    return new Date(tenant.subscription_ends_at) > new Date();
  }
  
  return false;
};
